import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link2, Trash2, CheckCircle2, XCircle, Clock, Building2 } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function ConnectedAccounts() {
  const [links, setLinks] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, e] = await Promise.all([
        api.get("/me/connected-accounts"),
        api.get("/me/partner-endorsements"),
      ]);
      setLinks(l.data.rows || []);
      setEndorsements(e.data.rows || []);
    } catch (ex) { console.error("load connected accounts failed", ex); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const disconnect = async (id, name) => {
    if (!confirm(`Disconnect from ${name}? Your EthicCred ID will no longer appear on their profile.`)) return;
    try { await api.delete(`/me/connected-accounts/${id}`); load(); toast.success("Disconnected"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const accept = async (id) => {
    try { await api.post(`/me/partner-endorsements/${id}/accept`); load(); toast.success("Endorsement accepted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    if (!confirm("Reject this endorsement? It will be sealed from your passport.")) return;
    try { await api.post(`/me/partner-endorsements/${id}/reject`); load(); toast.success("Rejected"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pending = endorsements.filter((e) => e.status === "pending");
  const decided = endorsements.filter((e) => e.status !== "pending");

  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="connected-accounts-page">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 sm:px-10 py-14">
        <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow"><Link2 className="w-3.5 h-3.5 text-ec-emerald" /> Connected accounts</div>
        <h1 className="font-display text-5xl font-black tracking-tight leading-[0.95] mt-5">Where your EthicCred ID appears.</h1>
        <p className="text-ec-ink-soft mt-4 max-w-2xl">Partner platforms that display your passport, plus any endorsements they've sent you to accept.</p>

        {/* Pending endorsements */}
        <h2 className="font-display text-2xl font-black tracking-tight mt-14 mb-4">Pending endorsements {pending.length > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 bg-ec-emerald text-ec-ink text-xs font-bold rounded-full">{pending.length}</span>}</h2>
        {pending.length === 0 && !loading && <div className="text-sm text-ec-ink-soft italic">No pending endorsements. New ones from any connected partner will appear here first.</div>}
        <div className="space-y-3">
          {pending.map((e) => (
            <Card key={e.id} className="rounded-sm border-ec-line" data-testid={`endorsement-${e.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-black text-lg">{e.role}</span>
                      <span className="text-ec-ink-soft">at</span>
                      <span className="font-bold">{e.company}</span>
                    </div>
                    <div className="eyebrow text-ec-ink-soft mt-1">via {e.partner_slug} · from {e.employer_name}</div>
                    <div className="flex flex-wrap gap-4 text-xs text-ec-ink-soft mt-3 font-mono-plex">
                      <span>{e.start_date} → {e.end_date || "present"}</span>
                      <span>★ {e.rating} / 5</span>
                    </div>
                    {e.feedback && <blockquote className="mt-3 pl-3 border-l-2 border-ec-emerald/40 italic text-sm text-ec-ink">"{e.feedback}"</blockquote>}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button onClick={() => accept(e.id)} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid={`accept-${e.id}`}><CheckCircle2 className="w-4 h-4" />Accept</Button>
                    <Button onClick={() => reject(e.id)} variant="outline" className="rounded-sm border-ec-error/30 text-ec-error hover:bg-ec-error/10 gap-2" data-testid={`reject-${e.id}`}><XCircle className="w-4 h-4" />Reject</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Linked partners */}
        <h2 className="font-display text-2xl font-black tracking-tight mt-14 mb-4">Linked partners</h2>
        {links.length === 0 && !loading && <div className="text-sm text-ec-ink-soft italic">No partner platforms are currently showing your EthicCred ID.</div>}
        <div className="grid sm:grid-cols-2 gap-3">
          {links.map((l) => (
            <Card key={l.id} className="rounded-sm border-ec-line" data-testid={`link-${l.id}`}>
              <CardContent className="p-5 flex items-start gap-3">
                <div className="w-10 h-10 bg-ec-ink text-white flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{l.partner_name || l.partner_slug}</div>
                  <div className="eyebrow text-ec-ink-soft">Linked {new Date(l.created_at).toLocaleDateString()}</div>
                  <div className="font-mono-plex text-[11px] text-ec-ink mt-1">#{l.ethiccred_id}</div>
                </div>
                <Button onClick={() => disconnect(l.id, l.partner_name || l.partner_slug)} variant="outline" size="sm" className="rounded-sm border-ec-error/30 text-ec-error hover:bg-ec-error/10" title="Disconnect" data-testid={`disconnect-${l.id}`}><Trash2 className="w-3 h-3" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Decided history */}
        {decided.length > 0 && (
          <>
            <h2 className="font-display text-2xl font-black tracking-tight mt-14 mb-4">History</h2>
            <div className="space-y-2">
              {decided.map((e) => (
                <div key={e.id} className="flex items-center gap-3 border border-ec-line bg-ec-soft px-4 py-2.5 text-sm rounded-sm">
                  {e.status === "accepted" ? <CheckCircle2 className="w-4 h-4 text-ec-emerald" /> : <XCircle className="w-4 h-4 text-ec-error" />}
                  <span className="font-bold">{e.role}</span>
                  <span className="text-ec-ink-soft">at {e.company}</span>
                  <span className="ml-auto eyebrow text-ec-ink-soft">{e.status}</span>
                  <span className="eyebrow text-ec-ink-soft"><Clock className="w-3 h-3 inline mr-1" />{e.decided_at ? new Date(e.decided_at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
